import { useId, useState, type ReactNode } from 'react'
import { ChevronDown, LockKeyhole } from 'lucide-react'

import { Button } from '../../ui/Button/Button'
import panel from './SettingsSection.module.css'

/**
 * Read-only NexHealth details for Owners and Admins, closed until asked for
 * so the editable settings above stay the focus. Callers render it only for
 * `canViewClinicTechnicalDetails`.
 */
export function TechnicalDetailsPanel({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string
  title: string
  description: ReactNode
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const bodyId = useId()
  const titleId = useId()
  return (
    <section
      className={`${panel.panel} ${panel.technicalPanel}`}
      aria-labelledby={titleId}
    >
      <header className={panel.panelHeader} data-collapsed={!open || undefined}>
        <div className={panel.heading}>
          <span className={panel.eyebrow}>{eyebrow}</span>
          <h2 id={titleId}>{title}</h2>
          <p>{description}</p>
          <span className={panel.audience}>
            <LockKeyhole size={12} aria-hidden="true" /> Owners and Admins only
            · Read-only
          </span>
        </div>
        <div className={panel.headerActions}>
          <Button
            type="button"
            variant="secondary"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen((current) => !current)}
          >
            {open ? 'Hide details' : 'Show details'}
            <ChevronDown
              size={16}
              aria-hidden="true"
              className={panel.chevron}
              data-open={open || undefined}
            />
          </Button>
        </div>
      </header>
      <div id={bodyId} hidden={!open} className={panel.technicalBody}>
        {open ? children : null}
      </div>
    </section>
  )
}
